import { useEffect, useRef } from 'react';
import { subscribeToEvent } from '../services/kaiSocket';
import {
  createSyncdataListener,
  type SyncdataHandlers,
  type SyncdataListenerOptions,
} from '../services/syncdataListener';

/**
 * React hook to run the syncdata listener for the lifetime of the component.
 * Use in a top-level layout or provider so it's initialized once.
 *
 * @example
 * useSyncdataListener({
 *   onPut: (modelName, message) => {
 *     queryClient.invalidateQueries({ queryKey: [modelName, message.data.id] });
 *   },
 *   onPost: (modelName, message) => { ... },
 *   onDelete: (modelName, message) => { ... },
 * });
 */
export function useSyncdataListener(
  handlers: SyncdataHandlers,
  options?: SyncdataListenerOptions,
): void {
  const handlersRef = useRef(handlers);
  const optionsRef = useRef(options);
  handlersRef.current = handlers;
  optionsRef.current = options;

  useEffect(() => {
    return createSyncdataListener(
      subscribeToEvent,
      {
        getModelName: (django) => handlersRef.current.getModelName?.(django),
        onPost: (model, msg) => handlersRef.current.onPost?.(model, msg),
        onPut: (model, msg) => handlersRef.current.onPut?.(model, msg),
        onDelete: (model, msg) => handlersRef.current.onDelete?.(model, msg),
        onActionFetch: (model, msg) => handlersRef.current.onActionFetch?.(model, msg),
      },
      optionsRef.current ?? {},
    );
  }, [subscribeToEvent]);
}
